import type { DataTableColumnGridTemplates } from './layout-types.js';
import {
  getColumnTemplateEntry,
  getTargetTemplate,
  processBuiltinColumn,
} from './layout-utils.js';
import { useDataTableConfigElement } from '../../../hooks/useDataTableConfig.js';
import {
  DATA_TABLE_DEFAULT_COLUMN_WIDTH,
  DATA_TABLE_ROW_HEIGHT_MAP,
  DATA_TABLE_SPACER_COLUMN_ID,
  DATA_TABLE_ROW_BORDER,
  DATA_TABLE_MIN_WIDTH,
} from '../../constants.js';
import type {
  Column,
  ColumnDef,
  Header,
  InitialTableState,
  Table,
  TableFeature,
  TableOptions,
} from '../../hooks/useTable/types.js';
import {
  assignRowMethods,
  getMemoOptions,
} from '../../hooks/useTable/utils.js';
import type { DataTableRowData } from '../../public.api.js';
import { memo } from '../../tanstack-table-core-override/src/index.js';

export const SPACER_COLUMN: ColumnDef<unknown> = {
  id: DATA_TABLE_SPACER_COLUMN_ID,
  header: () => undefined,
  cell: () => undefined,
  enableSorting: false,
  enablePinning: false,
  enableGrouping: false,
  enableUserActions: false,
  enableResizing: false,
  enableHiding: false,
  meta: {
    builtin: true,
    builtinPlacement: 'suffix',
    hasCustomCellRenderer: true,
    hasCustomHeaderRenderer: true,
    spacer: true,
    cellDeps: () => {
      // empty dependency to avoid unnecessary re-renders
      return [];
    },
  },
};

export const Layout: TableFeature = {
  getInitialState(state?: InitialTableState) {
    return {
      columnMeasureInfo: {},
      observedHeaderHeight: 0,
      ...state,
    };
  },

  createHeader<TData extends DataTableRowData, TValue>(
    header: Header<TData, TValue>,
    table: Table<TData>,
  ): void {
    /**
     * Allows to get the gridPosition of a specific header cell in order
     * to place it into the correct place in the template and span it across the correct
     * elements.
     */
    header.getGridPosition = memo(
      (headers) => [headers],
      (headers) => {
        // Get the start of the column index the header should be rendered into,
        // this should be equal to the index of the header cell in the loop passed
        // to this function.
        let gridColumnStart = 0;
        for (const headerInGroup of headers ?? []) {
          if (headerInGroup === header) {
            break;
          }
          gridColumnStart = gridColumnStart + headerInGroup.colSpan;
        }

        // The header already exposes a colSpan value, which can be used to span
        // the header cell element from the start position to the end of the
        // header group.
        const colSpan = header.colSpan;

        // Get the start of the index as this is actually the defined
        // column nesting level. If the column is not nested, it will get a
        // depth of 0, and nesting levels increase going deeper.
        const gridRowStart = header.column.depth;

        // Get the end from the headers depth, as this is where it would be rendered
        // within the tanstack table. As nested columns would be rendered in the
        // lower headerrow. The strato approach here is different, as the header
        // cell should span both header rows
        const gridRowEnd = header.depth;

        // return the gridColumn positioning. As the grid column positioning in
        // css is One-based, it needs an offset of 1 in every entry.
        return {
          gridColumnStart: gridColumnStart + 1,
          gridColumnEnd: `span ${colSpan}`,
          gridRowStart: gridRowStart + 1,
          gridRowEnd: `span ${gridRowEnd - gridRowStart}`,
        };
      },
      getMemoOptions(table.options, 'debugHeaders', 'header.getGridPosition'),
    );
  },

  createColumn<TData extends DataTableRowData>(
    column: Column<TData, unknown>,
    table: Table<TData>,
  ): void {
    column.getGridColumnPosition = (columnIndex?: number) => ({
      // Offset the index by one as the CSS grid column is a one based position.
      gridColumnStart: (columnIndex ?? 0) + 1,
      gridColumnEnd: 'span 1',
    });
  },

  createTable<TData extends DataTableRowData>(table: Table<TData>): void {
    assignRowMethods(table, {
      getGridRowPosition(rowIndex: number = -1) {
        return {
          // Offset the index by one as the CSS grid column is a one based position.
          gridColumnStart: 1,
          // The row should span all columns.
          gridColumnEnd: 'row-end',

          // The CSS grid column is a one based position, so we need to offset the index by one.
          // To account for the virtualized rows at the start of the grid, a spacer row is inserted.
          // Therefore we need to offset the index by two in total
          gridRowStart: rowIndex + 2,
          // The row should span only one line from the start edge.
          gridRowEnd: 'span 1',
        };
      },
    });

    table.getHasFractionalTemplate = () => {
      const template = table.getGridTemplateColumns();
      return template.includes('fr');
    };

    table.getVisibleHeaderGroupsCount = () => {
      if (table.getVisualVariantOptions().headers === 'hidden') {
        return 0;
      }

      return table.getHeaderGroups().length;
    };

    table.setObservedHeaderHeight = (height: number) => {
      if (height !== table.getState().observedHeaderHeight) {
        table.setState((state) => ({
          ...state,
          observedHeaderHeight: height,
        }));
      }
    };

    table.getHeaderSize = (
      headerId: string,
    ): {
      width: number;
      height: number;
    } => {
      const escapedId = CSS.escape(headerId);
      const headerSize = table
        .getRowVirtualizationContainer()
        ?.querySelector(
          `[role="columnheader"][data-headerid=${escapedId}], [role="columnheader"][data-columnid=${escapedId}], [role="presentation"][data-columnid=${escapedId}]`,
        )
        ?.getBoundingClientRect();

      if (headerSize === undefined) {
        // Use some standard value if no resize observer is there
        return {
          width: DATA_TABLE_DEFAULT_COLUMN_WIDTH,
          height: table.estimateRowHeight({ isHeaderRow: true }),
        };
      }

      return {
        width: headerSize.width,
        height: headerSize.height,
      };
    };

    table.getColumnOrHeaderWidth = (columnOrHeaderId: string): number => {
      const hasHiddenHeaders =
        table.getVisualVariantOptions().headers === 'hidden';

      // saved width that was stored upon measuring
      const storedWidth = table.getMeasuredColumnWidth(columnOrHeaderId);

      // early exit if all headers are visible and the column is a leaf column
      if (!hasHiddenHeaders && storedWidth !== undefined) {
        return storedWidth;
      }

      const escapedId = CSS.escape(columnOrHeaderId);
      // If all headers are hidden, take the cell in the first rendered row to retrieve the width since all other cells
      // in the same column should have the same width.
      // If the header is not in the virtualizer (because it is a parent column for instance), try to retrieve the height from the rendered element directly.
      const selectorString = hasHiddenHeaders
        ? `[role="cell"][data-columnid="${escapedId}"]`
        : `[role="columnheader"][data-headerid="${escapedId}"], [role="columnheader"][data-columnid="${escapedId}"], [role="presentation"][data-columnid="${escapedId}"]`;

      const columnWidth = table
        .getRowVirtualizationContainer()
        ?.querySelector(selectorString)
        ?.getBoundingClientRect().width;

      return columnWidth ?? DATA_TABLE_DEFAULT_COLUMN_WIDTH;
    };

    table.getTotalHeaderHeight = memo(
      () => [
        table.getVisualVariantOptions(),
        table.getVisibleLeafColumnsWithoutBuiltin(),
        table.getHeaderGroups(),
        table.getState().observedHeaderHeight,
      ],
      (
        visualVariantOptions,
        visibleLeafColumns,
        tableHeaderGroups,
        observedHeaderHeight,
      ) => {
        // If the headers are hidden, we can opt out earlier.
        if (
          visualVariantOptions.headers === 'hidden' ||
          visibleLeafColumns.length === 0 ||
          (tableHeaderGroups.length === 1 &&
            tableHeaderGroups.at(0)?.headers.length === 0)
        ) {
          return 0;
        }

        if (observedHeaderHeight > 0) {
          return observedHeaderHeight;
        }

        // if the measured header height is still undefined or 0, estimate it based on row density and amount of header groups,
        // so that the rowVirtualizer can return the most accurate height possible for the first render
        // this prevents an unnecessary vertical scrollbar which would cause wrong column measurements on initial render
        // the header's bottom border is actually 2px, estimateRowHeight only considers 1px so we need to add 1px more
        const bottomBorderOffset = DATA_TABLE_ROW_BORDER;
        const fallbackHeight =
          table.estimateRowHeight({ isHeaderRow: true }) *
            table.getVisibleHeaderGroupsCount() +
          bottomBorderOffset;

        return fallbackHeight;
      },
      getMemoOptions(table.options, 'debugTable', 'table.getTotalHeaderHeight'),
    );

    table.estimateRowHeight = memo(
      (props) => [
        props?.index,
        props?.isHeaderRow,
        table.getVisualVariantOptions(),
      ],
      (index, isHeaderRow, { rowDensity, rowSeparation }) => {
        const rowHeight = DATA_TABLE_ROW_HEIGHT_MAP[rowDensity];
        const paginationFlatRows = table.getPaginationRowModel().flatRows;
        const isLastRowOnPage = index === paginationFlatRows.length - 1;
        // only consider the border if the index is defined or the estimate is for a header row
        // when estimating rows for an empty table, the index will be undefined and there are no vertical dividers
        const addBorder = index !== undefined || isHeaderRow === true;

        if (
          addBorder === false ||
          rowSeparation === 'none' ||
          isLastRowOnPage
        ) {
          return rowHeight;
        }

        // add 1px border if rowSeparation is enabled or if this is a header row
        return rowHeight + DATA_TABLE_ROW_BORDER;
      },
      getMemoOptions(table.options, 'debugTable', 'table._estimateRowHeight'),
    );

    table.getHeaderGridTemplateRows = () => {
      switch (table.getVisibleHeaderGroupsCount()) {
        case 0:
          return '[body-start]';
        case 1:
          return '[header-start] max-content [header-end body-start]';
        default:
          return '[header-start] max-content max-content [header-end body-start]';
      }
    };

    table.getGridTemplateRows = memo(
      () => {
        const { getVirtualItems, range } = table.getRowVirtualizer() ?? {};

        return [
          getVirtualItems?.(),
          range?.startIndex,
          range?.endIndex,
          table.getVisibleHeaderGroupsCount(),
          table.options.data.length,
        ];
      },
      (virtualItems): string => {
        const measurementsCache = table.getRowVirtualizer()?.measurementsCache;
        const headerRows = table.getHeaderGridTemplateRows();

        if (!measurementsCache || !virtualItems?.length) {
          return headerRows;
        }

        // Determine the template rows for the body, based on the currently rendered rows
        const bodyRows = virtualItems
          .map((virtualItem) => {
            // The virtual item size is the most up to date value as it is measured during rendering,
            // while the measurementsCache can be outdated until the next render.
            // Therefore, the virtual item size is preferred to retrieve the row height.
            const rowHeight =
              virtualItem.size ??
              table.estimateRowHeight({ index: virtualItem.index });
            return `minmax(min-content, ${rowHeight}px)`;
          })
          .join(' ');

        // Add spacer row to account for virtualized rows above the first rendered row
        const firstVirtualItem = virtualItems[0];
        let spacerRowHeight = 0;

        if (firstVirtualItem.index > 0) {
          const scrollPaddingStart = table.getTotalHeaderHeight();
          // Calculate the height by subtracting the scroll padding (header height) from the starting offset of the first visible item.
          spacerRowHeight = firstVirtualItem.start - scrollPaddingStart;
        }

        // combine header, spacer and body rows into a grid row template
        return `${headerRows} ${spacerRowHeight}px ${bodyRows} [body-end]`;
      },
      getMemoOptions(table.options, 'debugTable', 'table.getGridTemplateRows'),
    );

    table.getGridTemplateColumns = memo(
      () => {
        const state = table.getState();
        return [
          state.columnSizing,
          state.columnSizingInfo,
          state.columnPinning,
          state.columnOrder,
          state.columnVisibility,
          table.getSubRowIndicatorColumnId(),
          JSON.stringify([...table.getMeasuredColumnWidths()]),
        ];
      },
      (columnSizing, columnSizingInfo) => {
        const leftVisibleColumns = table.getLeftVisibleLeafColumns();
        const centerVisibleColumns = table.getCenterVisibleLeafColumns();
        const rightVisibleColumns = table.getRightVisibleLeafColumns();

        if (
          leftVisibleColumns === undefined ||
          centerVisibleColumns === undefined ||
          rightVisibleColumns === undefined ||
          columnSizing === undefined ||
          columnSizingInfo === undefined
        ) {
          return '';
        }

        /**
         * Gets the measured column width if possible.
         * Min/max constraints are already considered during measurement. (see columnVirtualizer measureElement)
         *
         * @param columnId - ID of the column
         * @param isFractionWidth - whether the width is a fraction width
         * @returns measured width in pixels or undefined if not measured yet
         */
        function getMeasuredWidth(
          columnId: string,
          isFractionWidth: boolean = false,
        ): number | undefined {
          // If there are no rows (e.g. loading state), measure the header cell instead
          if (table.getRowModel().rows.length === 0) {
            const headerCell = table
              .getColumnVirtualizer()
              ?.scrollElement?.querySelector(
                `[role="columnheader"][data-columnid="${CSS.escape(
                  columnId,
                )}"]`,
              );
            const headerWidth = headerCell?.getBoundingClientRect().width;

            if (headerWidth === undefined) {
              return DATA_TABLE_MIN_WIDTH;
            }

            // Round up slightly for non-fraction widths to avoid rendering an ellipsis due to subpixel rendering issues
            // don't round for fraction widths to avoid rendering an unnecessary scrollbar for fullWidth tables
            return isFractionWidth
              ? headerWidth
              : Math.ceil(headerWidth * 10) / 10;
          }

          return table.getMeasuredColumnWidth(columnId);
        }

        // Grid templates for left, center and right pinned columns
        const columnGridTemplates: DataTableColumnGridTemplates = {
          left: [],
          center: [],
          right: [],
        };

        const orderedVisibleColumns = [
          ...leftVisibleColumns,
          ...centerVisibleColumns,
          ...rightVisibleColumns,
        ];

        // To generate the grid template columns, we need to iterate through all visible columns in order
        for (const column of orderedVisibleColumns) {
          // Handle built-in columns (prefix, suffix, spacer)
          const isBuiltinColumn = processBuiltinColumn(
            column,
            table.getVisualVariantOptions().rowDensity,
            table.options.rowThresholds,
            columnGridTemplates,
          );

          if (isBuiltinColumn) {
            continue;
          }

          // Handle width configuration for standard column
          const entry = getColumnTemplateEntry(
            column,
            columnSizing,
            columnSizingInfo,
            getMeasuredWidth,
          );

          if (entry !== undefined) {
            const targetTemplate = getTargetTemplate(
              column.getIsPinned(),
              columnGridTemplates,
            );

            targetTemplate.push(entry);
          }
        }

        const combinedGridTemplates = [
          ...columnGridTemplates.left,
          ...columnGridTemplates.center,
          ...columnGridTemplates.right,
        ];

        return `${combinedGridTemplates.join(' ')} [row-end]`;
      },
      getMemoOptions(
        table.options,
        'debugTable',
        'table.getGridTemplateColumns',
      ),
    );
  },
};

export function useLayout<TData>(
  props: { fullWidth?: boolean; fullHeight?: boolean },
  options: TableOptions<TData>,
): void {
  const configFullWidth = useDataTableConfigElement('fullWidth');
  const configFullHeight = useDataTableConfigElement('fullHeight');

  options.layout = {
    ...options.layout,
    fullWidth: props.fullWidth ?? configFullWidth,
    fullHeight: props.fullHeight ?? configFullHeight,
  };
}
